import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import LinearProgress from '@mui/material/LinearProgress';
import { Pagination } from '../../../../model/pagination';
import { IIrpPagedResponse } from '../../../../model/scp/irp/response/irp-paged-response';
import { IIrpPageRequest } from '../../../../model/scp/irp/request/irp-paged-request';
import IrpAppService from '../../../../application/platform/scp/irp/irp-app-service';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { DefaultDialogProps } from '../../../../components/default-dialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import PreviewIcon from '@mui/icons-material/Preview';
import SettingsIcon from '@mui/icons-material/Settings';
import { useBaseLayout } from '../../../../components/context/use-base-layout';
import { dataGridLocaleConfig } from '../../../../config/data-grid-locale';

export interface IIrpDataTableProps {
    parameters: IIrpPageRequest;
    searchTrigger?: number;
};
interface IIrpDataTableMenuContextProp {
    irp: IIrpPagedResponse,
    loadData: (parameters: IIrpPageRequest) => void
};
function MenuPopupState({ props }: { props: IIrpDataTableMenuContextProp }) {
    const { showDialog, showMessage, showLoading } = useBaseLayout();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const gotToDetails = () => {
        handleClose();
        window.open(props.irp.url, '_blank');
    }

    return (
        <div>
            <IconButton
                color="primary"
                id="basic-button"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}>
                <SettingsIcon />
            </IconButton>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                <MenuItem onClick={gotToDetails}>
                    <ListItemIcon>
                        <PreviewIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Acessar</ListItemText>
                </MenuItem>
                <MenuItem onClick={async () => {
                    handleClose();
                    navigate(`/intencao-registro-preco/publicar/${props.irp.id}`);
                }}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Editar</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                    showDialog({
                        title: 'Pergunta', message: 'Deseja realmente excluir a intenção de registro de preço? Caso deseje excluir preencha a justificativa abaixo:', onYesClick: (justification: string) => {
                            if (justification !== undefined && justification.length > 0) {
                                (async () => {
                                    showLoading({ open: true });
                                    await IrpAppService.deleteIrp(justification, props.irp.id);
                                    props.loadData({} as IIrpPageRequest);
                                    showLoading({ open: false });
                                    showMessage({ message: 'Intenção de registro de preço excluída com sucesso', type: 'success', open: true });
                                })();
                            } else {
                                showMessage({ message: 'Informe a justificativa para excluir a intenção de registro de preço', type: 'warning', open: true });
                            }
                        }, inputProps: {
                            rows: 4
                        }
                    } as DefaultDialogProps);

                }}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Excluir</ListItemText>
                </MenuItem>
            </Menu>
        </div>
    );
}

export default function IrpDataTable({ props }: { props: IIrpDataTableProps }) {
    const [response, setResponse] = useState<Pagination<IIrpPagedResponse>>(new Pagination<IIrpPagedResponse>);
    const [irpDataTableProps, setIrpDataTableProps] = useState<IIrpDataTableProps>(props);
    const [loadMoreData, setLoadMoreData] = useState(true);

    const dateFormatter = new Intl.DateTimeFormat('pt-BR');

    const loadData = (parameters: IIrpPageRequest) => {
        (async () => {
            if (parameters !== undefined) {
                setLoadMoreData(true);
                const data = await IrpAppService.listIrp(parameters);
                setResponse(data);
                setLoadMoreData(false);
            }
        })();
    }

    const columns: GridColDef<IIrpPagedResponse>[] = [
        { field: 'id', headerName: 'Id', width: 90, sortable: false },
        {
            field: 'numberIrp',
            headerName: 'Nº IRP',
            flex: 1,
            minWidth: 130,
            sortable: false
        },
        {
            field: 'processNumber',
            headerName: 'Nº Processo',
            flex: 1,
            minWidth: 140,
            sortable: false
        }, {
            field: 'year',
            headerName: 'Exercício',
            minWidth: 100,
            sortable: false
        },
        {
            field: 'objectIrp',
            headerName: 'Objeto',
            flex: 1,
            minWidth: 200,
            sortable: false
        },
        {
            field: 'modality',
            headerName: 'Modalidade',
            flex: 1,
            sortable: false
        },
        {
            field: 'finalInterestManifestationDate',
            headerName: 'Dt. Limite Man.',
            flex: 1,
            sortable: false,
            valueFormatter: ({ value }) => value ? dateFormatter.format(new Date(value)) : '',
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 100,
            align: "center",
            headerAlign: "center",
            sortable: false,
            renderCell: (params) => {
                return (
                    <MenuPopupState props={{ irp: params.row, loadData: loadData }}></MenuPopupState>
                );
            }
        }
    ];

    useEffect(() => {
        loadData(props.parameters);
        setIrpDataTableProps(props);
    }, [props.parameters.page, props.searchTrigger]); // Re-executa quando página ou searchTrigger mudarem

    return (
        <React.Fragment>
            <DataGrid
                sx={{ width: '100%', minWidth: 0 }}
                rows={response.items}
                columns={columns}
                columnVisibilityModel={{ id: false }}
                paginationModel={{ page: irpDataTableProps?.parameters.page ?? 0, pageSize: response.pageSize }}
                rowCount={response.totalRecords}
                disableRowSelectionOnClick
                onPaginationModelChange={(model) => {
                    const newParameters = { ...irpDataTableProps.parameters, page: model.page };
                    setIrpDataTableProps({ ...irpDataTableProps, parameters: newParameters });
                    loadData(newParameters);
                }}
                paginationMode="server"
                components={{
                    LoadingOverlay: LinearProgress,
                }}
                loading={loadMoreData}
                pageSizeOptions={[response.pageSize]}
                localeText={dataGridLocaleConfig}
            />
        </React.Fragment>
    );
}
